import {
  MousePointer2,
  Plus,
  Crop,
  Pencil,
  Type,
  Square,
  Eraser,
  PaintBucket,
  Stamp,
  PenTool,
  Sparkles,
} from 'lucide-react';
import type { EditorTool } from '../types';
import type { CommandRegistry } from '../lib/command-registry';
import { useFormattedShortcut } from '../hooks/useCommandRegistry';

interface ToolPaletteProps {
  activeTool: EditorTool;
  onToolSelect: (tool: EditorTool) => void;
  /** Registry instance — drives the shortcut hint in tooltips so
   *  "Crop (C)" updates to whatever the user has remapped Crop to
   *  in Settings → Keyboard. Tier 3 Group C Phase 2. */
  registry: CommandRegistry;
  isMac: boolean;
}

const tools: {
  id: EditorTool;
  icon: React.ComponentType<{ className?: string }>;
  /** Display name without the shortcut hint — the hint is computed
   *  reactively from the command registry. */
  label: string;
  /** Registry command id whose shortcut drives the tooltip hint.
   *  Matches the id used in `buildEditorCommands`. */
  commandId: string;
}[] = [
  { id: 'move', icon: MousePointer2, label: 'Move', commandId: 'tool.move' },
  { id: 'marquee', icon: Plus, label: 'Select', commandId: 'tool.marquee' },
  { id: 'crop', icon: Crop, label: 'Crop', commandId: 'tool.crop' },
  { id: 'draw', icon: Pencil, label: 'Draw', commandId: 'tool.draw' },
  { id: 'text', icon: Type, label: 'Text', commandId: 'tool.text' },
  { id: 'shape', icon: Square, label: 'Shape', commandId: 'tool.shape' },
  { id: 'eraser', icon: Eraser, label: 'Eraser', commandId: 'tool.eraser' },
  { id: 'fill', icon: PaintBucket, label: 'Fill', commandId: 'tool.fill' },
  { id: 'retouch', icon: Stamp, label: 'Retouch', commandId: 'tool.retouch' },
  // Pen has no single-letter shortcut in buildEditorCommands (Tier
  // 2 Group A pen tool predates the registry; remains a constant
  // here until a follow-up wires `tool.pen` into the catalog).
  { id: 'pen', icon: PenTool, label: 'Pen', commandId: 'tool.pen' },
  // Slice is reachable via the `S` shortcut, the command palette, and the
  // "Slices" button on the Select tool's properties panel — it is
  // intentionally absent from the toolbar to preserve mobile space.
  { id: 'ai', icon: Sparkles, label: 'AI', commandId: 'tool.ai' },
];

export function ToolPalette({ activeTool, onToolSelect, registry, isMac }: ToolPaletteProps) {
  return (
    <div className="flex flex-col items-center gap-1 w-14 bg-background border-r border-border py-2 px-1">
      {tools.map((tool) => {
        const Icon = tool.icon;
        const isActive = activeTool === tool.id;
        return (
          <ToolPaletteButton
            key={tool.id}
            tool={tool}
            Icon={Icon}
            isActive={isActive}
            registry={registry}
            isMac={isMac}
            onSelect={() => onToolSelect(tool.id)}
          />
        );
      })}
    </div>
  );
}

interface ToolPaletteButtonProps {
  tool: { id: EditorTool; label: string; commandId: string };
  Icon: React.ComponentType<{ className?: string }>;
  isActive: boolean;
  registry: CommandRegistry;
  isMac: boolean;
  onSelect: () => void;
}

function ToolPaletteButton({
  tool,
  Icon,
  isActive,
  registry,
  isMac,
  onSelect,
}: ToolPaletteButtonProps) {
  const shortcut = useFormattedShortcut(registry, tool.commandId, isMac);
  // Drop the parenthetical when the command has no binding (user
  // unassigned it) — "Crop ()" reads as broken UI.
  const title = shortcut ? `${tool.label} (${shortcut})` : tool.label;
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      }`}
      onClick={onSelect}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}
